import { useMemo, useState } from 'react';
import { ArrowLeft, Tag } from '@phosphor-icons/react';
import { Link, useParams } from 'react-router-dom';
import { paths } from '../../routes/paths';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import { AdjustCategoryBudgetDialog } from '../../components/budget/AdjustCategoryBudgetDialog';
import { CategoryBudgetProgressBar } from '../../components/budget/CategoryBudgetProgressBar';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { Button } from '../../components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card';
import {
  useCategoryBudgetHistoryQuery,
  useCategoryBudgetStatusQuery,
} from '../../features/budgets/queries';
import {
  useCategoriesQuery,
  useExpensesInfiniteQuery,
} from '../../features/expenses/queries';
import { SectionSkeleton } from '../../components/common/SectionSkeleton';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { useMobilePageHeaderTitle } from '../../store/MobilePageHeaderContext';

function monthLabel(year, month) {
  const name = pt.dashboard.monthAbbr[month] || '';
  return pt.budget.monthLabel.replace('{month}', name).replace('{year}', year);
}

export function CategoryDetail() {
  const { id } = useParams();
  const today = useMemo(() => new Date(), []);
  const reference = useMemo(
    () => ({ year: today.getFullYear(), month: today.getMonth() + 1 }),
    [today],
  );

  const { data: categories = [] } = useCategoriesQuery();
  const category = useMemo(
    () => categories.find((c) => c.id === id) || null,
    [categories, id],
  );
  useMobilePageHeaderTitle(category?.name);

  const { data: status, isPending: statusPending, isFetching: statusFetching } =
    useCategoryBudgetStatusQuery(id, reference);
  const statusShell = useQueryShellState({
    data: status,
    isPending: statusPending,
    isFetching: statusFetching,
  });
  const { data: history = [] } = useCategoryBudgetHistoryQuery(id, reference);

  const { data: expensesPages } = useExpensesInfiniteQuery({
    category: id,
    year: reference.year,
    month: reference.month,
  });
  const expenses =
    expensesPages?.pages?.flatMap((page) => page.results || []) || [];

  const [adjusting, setAdjusting] = useState(false);

  const chartData = useMemo(
    () =>
      history.map((row) => ({
        label: `${pt.dashboard.monthAbbr[row.reference_month]}/${String(row.reference_year).slice(-2)}`,
        spent: Number(row.spent_eur || 0),
        budget: row.budget_eur ? Number(row.budget_eur) : null,
        status: row.status,
      })),
    [history],
  );

  if (statusShell.showBlockingSkeleton || !category) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to={paths.budget}
          className="hidden items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:inline-flex"
        >
          <ArrowLeft className="h-4 w-4" /> {pt.budget.drilldown.backLink}
        </Link>
        <SectionSkeleton variant="card-grid" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Link
        to={paths.budget}
        className="hidden items-center gap-1 text-sm text-muted-foreground hover:text-foreground md:inline-flex"
      >
        <ArrowLeft className="h-4 w-4" /> {pt.budget.drilldown.backLink}
      </Link>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span
                className="flex h-12 w-12 items-center justify-center rounded-full"
                style={{ backgroundColor: `${category.color || '#7C9E6F'}33` }}
              >
                <Tag
                  className="h-5 w-5"
                  style={{ color: category.color || '#7C9E6F' }}
                  aria-hidden
                />
              </span>
              <div>
                <CardTitle className="hidden md:block">{category.name}</CardTitle>
                <CardDescription>
                  {category.group_name || pt.categories.form.noGroup} ·{' '}
                  {monthLabel(reference.year, reference.month)}
                </CardDescription>
              </div>
            </div>
            <Button onClick={() => setAdjusting(true)}>
              {pt.budget.categories.adjustCta}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                {pt.budget.totals.budget}
              </p>
              <MoneyDisplay
                value={status?.budget_eur ? Number(status.budget_eur) : null}
                size="lg"
              />
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                {pt.budget.totals.spent}
              </p>
              <MoneyDisplay value={Number(status?.spent_eur || 0)} size="lg" />
            </div>
            <div>
              <p className="text-xs uppercase text-muted-foreground">
                {pt.budget.totals.remaining}
              </p>
              <MoneyDisplay
                value={
                  status?.remaining_eur !== null && status?.remaining_eur !== undefined
                    ? Number(status.remaining_eur)
                    : null
                }
                size="lg"
              />
            </div>
          </div>
          <div className="mt-4">
            <CategoryBudgetProgressBar
              status={status?.status || 'no_budget'}
              percentage={status?.spent_pct ?? 0}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {status?.projected_eur !== null && status?.projected_eur !== undefined
                ? `${pt.budget.totals.projected}: ${formatEUR(Number(status.projected_eur))}`
                : pt.budget.totals.noBudget}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {pt.budget.drilldown.historyTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem histórico disponível.</p>
          ) : (
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart accessibilityLayer={false} data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="label" stroke="#737373" fontSize={12} />
                  <YAxis stroke="#737373" fontSize={12} />
                  <Bar dataKey="spent" name={pt.budget.totals.spent} fill="#7C9E6F" />
                  <Bar dataKey="budget" name={pt.budget.totals.budget} fill="#D4A574" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {pt.budget.drilldown.expensesTitle}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {expenses.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {pt.budget.drilldown.noExpenses}
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-border">
              {expenses.map((expense) => (
                <li
                  key={expense.id}
                  className="flex items-center justify-between py-2 text-sm"
                >
                  <div>
                    <p className="font-medium text-foreground">
                      {expense.description || category.name}
                    </p>
                    <p className="text-xs text-muted-foreground">{expense.occurred_on}</p>
                  </div>
                  <MoneyDisplay value={Number(expense.amount)} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <AdjustCategoryBudgetDialog
        isOpen={adjusting}
        onClose={() => setAdjusting(false)}
        category={{ ...category, ...status }}
        currentBudget={status?.budget_eur ? Number(status.budget_eur) : null}
      />
    </div>
  );
}
